import { ScrollView, StyleSheet, View } from 'react-native'
import { Text, useTheme } from 'react-native-paper'
import { formatTimeOfDay } from '@/lib/dates'
import type { UserGeofenceActivity } from '@/types/movement'
import { ConditionalList } from '@/ui/ConditionalList'

interface LocationActivityTableProps {
    activity: UserGeofenceActivity[]
    isPending: boolean
    userColorMap?: Map<string, string>
}

export function LocationActivityTable({
    activity,
    isPending,
    userColorMap,
}: LocationActivityTableProps) {
    const theme = useTheme()

    return (
        <ConditionalList
            data={activity}
            isLoading={isPending}
            icon="home-map-marker"
            title="No activity"
            subtitle="No geofence events recorded for this location today"
        >
            <ScrollView style={styles.table}>
                <View
                    style={[styles.headerRow, { borderBottomColor: theme.colors.outlineVariant }]}
                >
                    <Text
                        style={[
                            styles.headerText,
                            styles.userColumn,
                            { color: theme.colors.onSurfaceVariant },
                        ]}
                    >
                        User
                    </Text>
                    <Text
                        style={[
                            styles.headerText,
                            styles.timeColumn,
                            { color: theme.colors.onSurfaceVariant },
                        ]}
                    >
                        Enter
                    </Text>
                    <Text
                        style={[
                            styles.headerText,
                            styles.timeColumn,
                            { color: theme.colors.onSurfaceVariant },
                        ]}
                    >
                        Exit
                    </Text>
                </View>

                {activity.map(row => {
                    const userColor = userColorMap?.get(row.userId)
                    return (
                        <View
                            key={row.userId}
                            style={[styles.row, { borderBottomColor: theme.colors.outlineVariant }]}
                        >
                            <Text
                                style={[
                                    styles.cellText,
                                    styles.userColumn,
                                    { color: userColor || theme.colors.onSurface },
                                ]}
                                numberOfLines={1}
                            >
                                {row.userName}
                            </Text>
                            <Text
                                style={[
                                    styles.cellText,
                                    styles.timeColumn,
                                    { color: theme.colors.primary },
                                ]}
                            >
                                {row.enterTime ? formatTimeOfDay(row.enterTime) : '\u2014'}
                            </Text>
                            <Text
                                style={[
                                    styles.cellText,
                                    styles.timeColumn,
                                    { color: theme.colors.error },
                                ]}
                            >
                                {row.exitTime ? formatTimeOfDay(row.exitTime) : '\u2014'}
                            </Text>
                        </View>
                    )
                })}
            </ScrollView>
        </ConditionalList>
    )
}

const styles = StyleSheet.create({
    table: {
        flex: 1,
        paddingTop: 8,
    },
    headerRow: {
        flexDirection: 'row',
        paddingVertical: 8,
        borderBottomWidth: 1,
    },
    headerText: {
        fontSize: 12,
        fontWeight: '600',
    },
    row: {
        flexDirection: 'row',
        paddingVertical: 10,
        borderBottomWidth: StyleSheet.hairlineWidth,
    },
    userColumn: {
        flex: 1,
        paddingRight: 8,
    },
    timeColumn: {
        width: 80,
        textAlign: 'center',
    },
    cellText: {
        fontSize: 14,
    },
})
